import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { ProgramStatisticsTab } from '@/components/programs/ProgramStatisticsTab'
import { ProgramStructureTab } from '@/components/programs/ProgramStructureTab'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { EditorJsField } from '@/components/editor/EditorJsField'
import {
  programAssignmentsApi,
  programsApi,
  type Program,
  type ProgramTheme,
  type ProgramThemeElement,
  type ThemePropagation,
} from '@/api/programs'
import { exercisesApi, type Exercise } from '@/api/exercises'
import { ExerciseFormDialog } from '@/components/exercises/ExerciseFormDialog'
import { ExerciseBankPickerDialog } from '@/components/exercises/ExerciseBankPickerDialog'
import { themeWordsOf } from '@/components/exercises/themeWords'
import { wordsApi } from '@/api/words'
import { usersApi } from '@/api/users'
import { groupsApi } from '@/api/groups'
import { useSubscriptionParameterFresh } from '@/hooks/useSubscriptionParameterFresh'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import type { EditorJsData, StudentGroup } from '@/types'

type WordOption = { id: number; label: string }

type RecipientOption = { type: 'student' | 'group'; id: number; label: string }

/**
 * Все группы учителя, а не первая страница.
 *
 * Список получателей ищется по уже загруженным опциям, а `GET /groups` без
 * `per_page` отдаёт двадцать: с двадцать первой группы назначить программу
 * было нельзя вовсе — в выпадающем списке её нет, и поиск по названию её не
 * находит. Обходим страницы так же, как `usersApi.listAllStudents`.
 */
async function listAllGroups(): Promise<StudentGroup[]> {
  const perPage = 100
  const groups: StudentGroup[] = []
  let page = 1
  let lastPage = 1

  do {
    const response = await groupsApi.getAll({ per_page: perPage, page })
    groups.push(...(response.data ?? []))
    lastPage = response.last_page || 1
    page += 1
    // Страховка от зацикливания, если бэкенд вернёт некорректный last_page
  } while (page <= lastPage && page <= 100)

  return groups
}

/**
 * Конструктор программы: разделы, темы и элементы тем (блоки теории и словарь).
 * Отсюда же программа назначается ученику или группе.
 */
export function ProgramEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const programId = Number(id)

  // Вкладка в URL: ссылкой на статистику шаблона делятся, и F5 не должен
  // возвращать на структуру
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'statistics' ? 'statistics' : 'structure'
  const { t } = useTranslation()

  const [program, setProgram] = useState<Program | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)


  const [elementTheme, setElementTheme] = useState<ProgramTheme | null>(null)
  // Кого затронет правка блока: учитель не видит со страницы каталога, что
  // тема уже у учеников, и правит её как черновик
  const [propagation, setPropagation] = useState<ThemePropagation | null>(null)
  const [elementType, setElementType] = useState<'theory' | 'vocabulary'>('theory')
  // Диалог задания — только карточка уже существующего задания: заводить
  // задания внутри программы нельзя, для этого есть страница банка заданий.
  // Поэтому `exercise` здесь всегда заполнен, режима создания больше нет.
  const [exerciseDialog, setExerciseDialog] = useState<
    { theme: ProgramTheme; exercise: Exercise } | null
  >(null)
  // Догрузка карточки задания: на время запроса показываем окно со скелетоном
  const [exerciseLoading, setExerciseLoading] = useState(false)
  // Выбор задания из банка: задание живёт в банке, тема на него ссылается
  const [bankPicker, setBankPicker] = useState<ProgramTheme | null>(null)
  const [elementTitle, setElementTitle] = useState('')
  const [elementBody, setElementBody] = useState<EditorJsData>({ blocks: [] })
  const [wordOptions, setWordOptions] = useState<WordOption[]>([])
  const [selectedWords, setSelectedWords] = useState<WordOption[]>([])
  const [savingElement, setSavingElement] = useState(false)
  const [editingElement, setEditingElement] = useState<ProgramThemeElement | null>(null)
  const [elementMenuAnchor, setElementMenuAnchor] = useState<null | HTMLElement>(null)
  const [elementMenuTarget, setElementMenuTarget] = useState<{ theme: ProgramTheme; element: ProgramThemeElement } | null>(null)
  const [deletingElement, setDeletingElement] = useState<{ theme: ProgramTheme; element: ProgramThemeElement } | null>(null)
  const [deletingTheme, setDeletingTheme] = useState<ProgramTheme | null>(null)
  // Ошибки диалогов держим отдельно: Alert страницы рисуется ПОД открытой
  // модалкой, и 422 матрицы типов пользователь не видел вовсе.
  const [elementError, setElementError] = useState<string | null>(null)

  const [assignOpen, setAssignOpen] = useState(false)
  const [recipients, setRecipients] = useState<RecipientOption[]>([])
  const [recipient, setRecipient] = useState<RecipientOption | null>(null)
  const [visibility, setVisibility] = useState<'hidden' | 'titles' | 'full'>('titles')
  const [assigning, setAssigning] = useState(false)
  const [recipientsLoading, setRecipientsLoading] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)

  /**
   * silent — обновление на месте, без скелетона. Скелетон размонтирует вкладку
   * структуры, а вместе с ней раскрытые темы и порядок, который пользователь
   * только что выставил перетаскиванием.
   */
  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (options?.silent !== true) setLoading(true)
    setError(null)
    try {
      setProgram(await programsApi.get(programId))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (options?.silent !== true) setLoading(false)
    }
  }, [programId, t])

  useEffect(() => {
    if (Number.isFinite(programId)) {
      void load()

      return
    }

    // /programs/abc: без этого страница навсегда оставалась в скелетоне
    setLoading(false)
    setError(t('common.error'))
  }, [load, programId, t])

  const isReadOnly = program?.is_system === true
  // Премиум-типы заданий: без параметра они заблокированы замком в конструкторе
  const premiumExercisesEnabled = useSubscriptionParameterFresh('premium_exercises_enabled')



  const handleDeleteTheme = async (theme: ProgramTheme) => {
    try {
      await programsApi.deleteTheme(programId, theme.id)
      void load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const searchWords = async (query: string) => {
    try {
      const result = await wordsApi.getWords({ search: query, per_page: 20 })
      setWordOptions(
        result.data.map((word) => ({
          id: word.id,
          label: word.translation ? `${word.word} — ${word.translation}` : word.word,
        })),
      )
    } catch {
      setWordOptions([])
    }
  }

  useEffect(() => {
    if (!programId || !elementTheme) {
      setPropagation(null)

      return
    }

    let cancelled = false
    void programsApi
      .themePropagation(programId, elementTheme.id)
      .then((data) => {
        if (!cancelled) setPropagation(data)
      })
      // Сводка — подсказка, а не условие сохранения: её недоступность не должна
      // мешать править программу
      .catch(() => {
        if (!cancelled) setPropagation(null)
      })

    return () => {
      cancelled = true
    }
  }, [programId, elementTheme])

  const handleSaveElement = async () => {
    if (!elementTheme) return
    setSavingElement(true)
    setElementError(null)
    try {
      // Матрица типов на бэкенде: теория требует тело и запрещает слова,
      // словарь — наоборот. Отправляем ровно то, что положено типу.
      const body = elementType === 'theory' ? (elementBody as unknown as Record<string, unknown>) : undefined
      const wordIds = elementType === 'vocabulary' ? selectedWords.map((word) => word.id) : undefined

      if (editingElement) {
        // Тип при правке не меняется — сервер его и не принимает
        await programsApi.updateElement(elementTheme.id, editingElement.id, {
          title: elementTitle || null,
          body_json: body,
          word_ids: wordIds,
        })
      } else {
        await programsApi.createElement(elementTheme.id, {
          type: elementType,
          title: elementTitle || null,
          body_json: body,
          word_ids: wordIds,
        })
      }
      setEditingElement(null)
      setElementTheme(null)
      setElementTitle('')
      setElementBody({ blocks: [] })
      setSelectedWords([])
      void load()
    } catch (err) {
      setElementError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSavingElement(false)
    }
  }

  const openCreateElement = (theme: ProgramTheme) => {
    setElementError(null)
    setEditingElement(null)
    setElementType('theory')
    setElementTitle('')
    setElementBody({ blocks: [] })
    setSelectedWords([])
    setElementTheme(theme)
  }

  /**
   * Задания, уже стоящие в теме: одно и то же задание не должно попасть в тему
   * дважды — в структуре это две одинаковые строки, а сервер отвечает 422.
   */
  const attachedExerciseIds = (theme: ProgramTheme | null): number[] =>
    (theme?.elements ?? []).flatMap((element) =>
      element.type === 'exercise' && element.exercise_id != null ? [element.exercise_id] : [],
    )

  /** Открыть карточку задания из строки блока */
  const openExercise = async (theme: ProgramTheme, exerciseId: number) => {
    // Окно-скелетон поднимаем до запроса: между кликом и карточкой лежит сеть,
    // и без него строка выглядит как «не нажалось» — учитель жмёт ещё раз
    setExerciseLoading(true)
    setError(null)
    try {
      setExerciseDialog({ theme, exercise: await exercisesApi.get(exerciseId) })
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setExerciseLoading(false)
    }
  }

  /** Открыть блок: у системной программы — на просмотр, у своей — на правку */
  const openElement = (theme: ProgramTheme, element: ProgramThemeElement) => {
    setElementError(null)
    setElementMenuAnchor(null)
    setEditingElement(element)
    setElementType(element.type === 'vocabulary' ? 'vocabulary' : 'theory')
    setElementTitle(element.title ?? '')
    setElementBody((element.body_json as unknown as EditorJsData) ?? { blocks: [] })
    setSelectedWords(
      (element.words ?? []).map((word) => ({
        id: word.id,
        label: word.translation ? `${word.text} — ${word.translation}` : word.text,
      })),
    )
    setElementTheme(theme)
  }

  const handleDeleteElement = async () => {
    if (!deletingElement) return
    const { theme, element } = deletingElement
    setDeletingElement(null)
    try {
      await programsApi.deleteElement(theme.id, element.id)
      void load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const openAssign = async () => {
    setAssignOpen(true)
    setAssignError(null)
    setRecipientsLoading(true)
    try {
      // Оба списка целиком: Autocomplete ищет по загруженным опциям, и
      // получатель, не попавший на первую страницу, был бы недоступен
      const [students, groups] = await Promise.all([
        usersApi.listAllStudents(),
        listAllGroups(),
      ])
      setRecipients([
        ...students.map((student): RecipientOption => ({
          type: 'student' as const,
          id: student.id,
          label: student.display_name || student.email || `#${student.id}`,
        })),
        ...groups.map((group): RecipientOption => ({
          type: 'group' as const,
          id: group.id,
          label: `${group.name} (${t('programs.group', 'группа')})`,
        })),
      ])
    } catch (err) {
      setAssignError(getErrorMessage(err) || t('common.error'))
    } finally {
      setRecipientsLoading(false)
    }
  }

  const handleAssign = async () => {
    if (!recipient) return
    setAssigning(true)
    setAssignError(null)
    try {
      const assignment = await programAssignmentsApi.assign(programId, {
        recipient_type: recipient.type,
        recipient_id: recipient.id,
        visibility_default: visibility,
      })
      setAssignOpen(false)
      setRecipient(null)
      navigate(ROUTES.PROGRAM_ASSIGNMENT(assignment.id))
    } catch (err) {
      // Занятый слот приходит как 422: у ученика уже есть активная программа
      setAssignError(getErrorMessage(err) || t('common.error'))
    } finally {
      setAssigning(false)
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Крошки известны до загрузки: скелетон на них оставлял бы
              пользователя без ответа на вопрос «где я» */}
          <PageBreadcrumbs items={[{ label: t('programs.title'), to: ROUTES.PROGRAMS }]} />
          <Skeleton variant="text" width="40%" height={44} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 1 }} />
        </Box>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs
          items={[{ label: t('programs.title'), to: ROUTES.PROGRAMS }, { label: program?.name ?? '' }]}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <IconButton aria-label={t('common.back', 'Назад')} onClick={() => navigate(ROUTES.PROGRAMS)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {program?.name}
          </Typography>
          {program?.level && <Chip size="small" label={program.level} />}
          {program?.is_system && <Chip size="small" color="primary" label={t('programs.systemBadge', 'EasyA')} />}
          <PageHelpPopover pageKey="programs" />
          <Box sx={{ ml: 'auto' }}>
            <Button variant="contained" onClick={openAssign}>
              {t('programs.assign', 'Назначить')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isReadOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('programs.systemReadOnly', 'Это программа платформы: её можно назначить или скопировать себе, но не изменить.')}
          </Alert>
        )}

        <Tabs value={tab} onChange={(_, value) => setSearchParams({ tab: value })} sx={{ mb: 2 }}>
          <Tab value="structure" label={t('programs.structure')} />
          <Tab value="statistics" label={t('programs.stats.tab')} />
        </Tabs>

        {tab === 'statistics' && programId > 0 && <ProgramStatisticsTab programId={programId} />}

        {tab === 'structure' && program !== null && (
          <ProgramStructureTab
            program={program}
            readOnly={isReadOnly}
            onOpenElement={openElement}
            onAddElement={openCreateElement}
            onOpenExercise={openExercise}
            onDeleteTheme={setDeletingTheme}
            onElementMenu={(anchor, theme, element) => {
              setElementMenuAnchor(anchor)
              setElementMenuTarget({ theme, element })
            }}
            onChanged={() => void load({ silent: true })}
            onError={setError}
          />
        )}

        <Dialog
          open={Boolean(elementTheme)}
          onClose={() => {
            setElementTheme(null)
            setEditingElement(null)
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            {/* Тип блока в шапке: у существующего блока он не меняется, и
                отдельное поле было мёртвым контролом */}
            {editingElement
              ? t(`programs.elementTypes.${elementType}`)
              : t('programs.addElementTo', 'Блок темы «{{theme}}»', { theme: elementTheme?.title ?? '' })}
            {editingElement && (
              <Typography variant="body2" color="text.secondary">
                {elementTheme?.title}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {elementError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {elementError}
              </Alert>
            )}
            {!isReadOnly && propagation !== null && propagation.assignments_total > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {t(
                  'programs.propagationHint',
                  'Тема назначена {{total}} раз, из них {{auto}} обновятся автоматически',
                  { total: propagation.assignments_total, auto: propagation.auto_update_count },
                )}
                {propagation.manual_count > 0 &&
                  ' ' +
                    t(
                      'programs.propagationManual',
                      'Остальные правили вручную или уже начали — их обновит учитель на странице назначения.',
                    )}
              </Alert>
            )}
            {/* Тип задаётся только при создании: у существующего блока он не
                меняется — копии в назначениях перевернулись бы вместе с ним */}
            {!editingElement && (
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>{t('programs.fields.elementType', 'Тип блока')}</InputLabel>
                <Select
                  label={t('programs.fields.elementType', 'Тип блока')}
                  value={elementType}
                  onChange={(e) => {
                    const next = e.target.value as 'theory' | 'vocabulary' | 'exercise'

                    // «Задание» — тот же шаг, только вместо конструктора
                    // открывается банк: задание не создаётся внутри темы, тема
                    // лишь ссылается на готовое задание банка
                    if (next === 'exercise') {
                      const theme = elementTheme
                      setElementTheme(null)
                      if (theme) setBankPicker(theme)

                      return
                    }

                    setElementType(next)
                  }}
                >
                  <MenuItem value="theory">{t('programs.elementTypes.theory', 'Теория')}</MenuItem>
                  <MenuItem value="vocabulary">{t('programs.elementTypes.vocabulary', 'Словарь темы')}</MenuItem>
                  <MenuItem value="exercise">{t('programs.elementTypes.exercise', 'Задание')}</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* В просмотре — текст, а не поле ввода: поле ловит фокус на клик и
                выглядит как приглашение править то, что править нельзя */}
            {isReadOnly ? (
              elementTitle.trim() !== '' && (
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {elementTitle}
                </Typography>
              )
            ) : (
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label={t('programs.fields.elementTitle', 'Заголовок блока')}
                value={elementTitle}
                onChange={(e) => setElementTitle(e.target.value)}
              />
            )}

            {elementType === 'theory' ? (
              <Box sx={{ mt: 2 }}>
                <EditorJsField
                  value={elementBody}
                  onChange={setElementBody}
                  label={t('programs.fields.theory', 'Теория')}
                  readOnly={isReadOnly}
                  readOnlySurface="paper"
                  extendedBlocks
                  hideLabel={isReadOnly}
                  // Картинка в теории идёт в квоту учителя тем же путём, что и
                  // картинки заданий: эндпоинт проверяет место ДО записи файла
                  imageUploader={isReadOnly ? undefined : (file) => exercisesApi.uploadImage(file)}
                  initDelayMs={100}
                />
              </Box>
            ) : isReadOnly ? (
              <Box sx={{ mt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {selectedWords.length === 0 ? (
                  <Typography color="text.secondary">{t('programs.themeEmpty')}</Typography>
                ) : (
                  selectedWords.map((word) => (
                    <Chip key={word.id} size="small" variant="outlined" label={word.label} />
                  ))
                )}
              </Box>
            ) : (
              <Autocomplete
                multiple
                options={wordOptions}
                value={selectedWords}
                sx={{ mt: 2 }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_, value) => value.length >= 2 && void searchWords(value)}
                onChange={(_, value) => setSelectedWords(value)}
                renderInput={(params) => (
                  <TextField {...params} label={t('programs.fields.words', 'Слова темы')} size="small" />
                )}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setElementTheme(null)
                setEditingElement(null)
              }}
            >
              {isReadOnly ? t('common.close') : t('common.cancel', 'Отмена')}
            </Button>
            {!isReadOnly && (
              <Button variant="contained" disabled={savingElement} onClick={handleSaveElement}>
                {t('common.save', 'Сохранить')}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <ExerciseBankPickerDialog
          open={bankPicker !== null}
          themeId={bankPicker?.id ?? 0}
          themeTitle={bankPicker?.title}
          attachedIds={attachedExerciseIds(bankPicker)}
          onClose={() => setBankPicker(null)}
          onSwitchType={(next) => {
            // Передумал уже в банке: возвращаем тот же шаг с обычным блоком
            const theme = bankPicker
            setBankPicker(null)
            if (theme) {
              openCreateElement(theme)
              setElementType(next)
            }
          }}
          onAttached={() => {
            setBankPicker(null)
            void load({ silent: true })
          }}
        />

        {/* Догрузка задания видна сразу: эталон — банк заданий (ExerciseBankPage) */}
        <Dialog open={exerciseLoading} maxWidth="md" fullWidth>
          <DialogTitle>{t('common.loading')}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={160} />
            </Box>
          </DialogContent>
        </Dialog>

        <ExerciseFormDialog
          open={exerciseDialog !== null}
          exercise={exerciseDialog?.exercise ?? null}
          themeWords={themeWordsOf(exerciseDialog?.theme)}
          premiumEnabled={premiumExercisesEnabled}
          // Витринное задание правит платформа, а не учитель: PATCH /exercises
          // на is_system отвечает 403. Читать readOnly только по программе было
          // мало — системное задание стоит и в обычной программе учителя, и
          // форма открывалась редактируемой, а введённое пропадало на сохранении
          readOnly={isReadOnly || exerciseDialog?.exercise?.is_system === true}
          onClose={() => setExerciseDialog(null)}
          onSaved={() => {
            // Из программы форма открывается только на уже существующем
            // задании: перечитываем структуру, чтобы подхватить новое название
            setExerciseDialog(null)
            void load({ silent: true })
          }}
        />

        <Menu
          anchorEl={elementMenuAnchor}
          open={Boolean(elementMenuAnchor)}
          onClose={() => setElementMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => elementMenuTarget && openElement(elementMenuTarget.theme, elementMenuTarget.element)}
          >
            {t('common.edit', 'Изменить')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setDeletingElement(elementMenuTarget)
              setElementMenuAnchor(null)
            }}
          >
            {t('common.delete', 'Удалить')}
          </MenuItem>
        </Menu>

        <Dialog open={Boolean(deletingTheme)} onClose={() => setDeletingTheme(null)} fullWidth maxWidth="xs">
          <DialogTitle>{t('programs.deleteTheme', 'Удалить тему')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                'programs.deleteThemeConfirm',
                'Тема «{{title}}» и все её блоки исчезнут из шаблона. В уже назначенных программах копия темы останется.',
                { title: deletingTheme?.title ?? '' },
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletingTheme(null)}>{t('common.cancel', 'Отмена')}</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                const theme = deletingTheme
                setDeletingTheme(null)
                if (theme) void handleDeleteTheme(theme)
              }}
            >
              {t('common.delete', 'Удалить')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deletingElement)} onClose={() => setDeletingElement(null)} fullWidth maxWidth="xs">
          <DialogTitle>{t('programs.deleteElement', 'Удалить блок темы')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                'programs.deleteElementConfirm',
                'Блок исчезнет из шаблона. В уже назначенных программах его копия останется.',
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletingElement(null)}>{t('common.cancel', 'Отмена')}</Button>
            <Button color="error" variant="contained" onClick={handleDeleteElement}>
              {t('common.delete', 'Удалить')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('programs.assignTitle', 'Назначить программу')}</DialogTitle>
          <DialogContent>
            {assignError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {assignError}
              </Alert>
            )}
            {recipientsLoading ? (
              <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, mt: 1 }} />
            ) : (
            <Autocomplete
              options={recipients}
              value={recipient}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id && option.type === value.type}
              onChange={(_, value) => setRecipient(value)}
              renderInput={(params) => (
                <TextField {...params} margin="dense" label={t('programs.fields.recipient', 'Кому назначить')} />
              )}
            />
            )}
            <FormControl fullWidth size="small" margin="dense">
              <InputLabel>{t('programs.fields.visibility', 'Видимость будущих тем')}</InputLabel>
              <Select
                label={t('programs.fields.visibility', 'Видимость будущих тем')}
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'hidden' | 'titles' | 'full')}
              >
                <MenuItem value="hidden">{t('programs.visibility.hidden', 'Скрыть будущие темы')}</MenuItem>
                <MenuItem value="titles">{t('programs.visibility.titles', 'Только заголовки')}</MenuItem>
                <MenuItem value="full">{t('programs.visibility.full', 'Открыть весь курс')}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
            <Button variant="contained" disabled={!recipient || assigning} onClick={handleAssign}>
              {t('programs.assign', 'Назначить')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
